import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import Layout from "../components/Layout";
import {
  FiTrash2,
  FiEdit,
  FiPlus,
  FiSave,
  FiClipboard,
} from "react-icons/fi";
import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";

const EMPTY = {
  FromRentslab: "",
  ToRentslab: "",
  toMore: false,
};

export default function RentDurations() {
  const navigate = useNavigate();

  // STATE
  const [records, setRecords] = useState([]);

  const [nextFrom, setNextFrom] = useState(1);
  const [hasToMore, setHasToMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);
  const [toMoreValue, setToMoreValue] = useState(null);

  // PAGINATION
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // SEARCH
  const [search] = useState("");

  // TOTAL RECORDS
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // TOAST
  const showToast = useCallback((msg, type = "success") => {
    if (type === "error") {
      toasts.error(msg);
    } else if (type === "warning") {
      toasts.warning(msg);
    } else if (type === "info") {
      toasts.info(msg);
    } else {
      toasts.success(msg);
    }
  }, []);

  // RESET FORM
  const resetForm = useCallback(
    (nf = null) => {
      const startFrom = nf || nextFrom || 1;

      setForm({
        FromRentslab: startFrom,
        ToRentslab: startFrom,
        toMore: false,
      });

      setEditId(null);
    },
    [nextFrom]
  );

  // AUTHENTICATION
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // FETCH ALL
  const fetchAll = useCallback(async () => {
    setLoading(true);

    try {
      const [r1, r2] = await Promise.all([
        API.get("/rent-durations", {
          params: {
            page,
            limit,
            search: search.trim(),
          },
        }),

        API.get("/rent-durations/meta/nextfrom"),
      ]);

      // RECORDS
      const data = r1.data?.data || [];
      setRecords(data);

      // PAGINATION
      const total = Number(r1.data?.total || 0);

      const pages = Number(
        r1.data?.totalPages || Math.ceil(total / limit)
      );

      setTotal(total);
      setTotalPages(pages);

      // NEXT FROM
      const next = Number(r2.data?.nextFrom || 1);

      setNextFrom(next);

      // TO MORE STATUS
      setHasToMore(Boolean(r2.data?.hasToMore));

      // GET 1000 FROM DATABASE RECORDS
      const toMoreRecord = data.find(
        (item) => Number(item.ToRentslab) === 1000
      );

      setToMoreValue(
        toMoreRecord ? Number(toMoreRecord.ToRentslab) : null
      );

      // ADD MODE
      // Don't overwrite edit form
      if (!editId) {
        setForm((prev) => ({
          ...prev,
          FromRentslab: next,
          ToRentslab: prev.toMore ? "" : next,
        }));
      }
    } catch (error) {
      console.error("fetchAll error:", error);

      showToast(
        error.response?.data?.message || "Failed to load records",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, editId, showToast]);

  // FETCH WHEN PAGE / LIMIT / SEARCH CHANGES
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      return;
    }

    fetchAll();
  }, [fetchAll]);

  // OPEN ADD MODAL
  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  // CLOSE ADD / EDIT MODAL
  const closeModal = () => {
    resetForm();
    setShowModal(false);
  };

  // EDIT
  const startEdit = (rec) => {
    const isOpenEnded = Number(rec.ToRentslab) === 1000;

    setEditId(rec.RentslabId);

    setForm({
      FromRentslab: rec.FromRentslab,
      ToRentslab: isOpenEnded ? "" : rec.ToRentslab,
      toMore: isOpenEnded,
    });

    // OPEN SAME MODAL FOR EDIT
    setShowModal(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // FROM DAYS CHANGE
  const handleFromChange = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      FromRentslab: value,

      // Automatically set To = From
      // only when not open-ended
      ToRentslab: prev.toMore ? "" : value,
    }));
  };

  // OPEN ENDED CHANGE
  const handleToMoreChange = (e) => {
    const isToMore = e.target.value === "1";

    setForm((prev) => ({
      ...prev,

      toMore: isToMore,

      ToRentslab: isToMore ? "" : prev.FromRentslab,
    }));
  };

  // SUBMIT
  const handleSubmit = async () => {
    const from = Number(form.FromRentslab);

    const to = form.toMore ? 1000 : Number(form.ToRentslab);

    // FROM VALIDATION
    if (!Number.isInteger(from) || from < 1) {
      showToast("From Days is required", "error");
      return;
    }

    // TO VALIDATION
    if (!form.toMore) {
      if (!Number.isInteger(to) || to < from) {
        showToast("To Days must be ≥ From Days", "error");
        return;
      }
    }

    // PREVENT CREATING ANOTHER OPEN-ENDED SLAB
    if (!editId && form.toMore && hasToMore) {
      showToast('A "To More Days" slab already exists', "error");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        FromRentslab: from,
        ToRentslab: to,
        toMore: form.toMore,
      };

      // UPDATE
      if (editId) {
        await API.put(`/rent-durations/${editId}`, payload);

        showToast("Rent duration updated");
      }

      // INSERT
      else {
        await API.post("/rent-durations", payload);

        showToast("Rent duration added");
      }

      // RESET TO FIRST PAGE
      setPage(1);

      // REFRESH RECORDS
      await fetchAll();

      // CLOSE MODAL
      closeModal();
    } catch (error) {
      console.error("Submit error:", error);

      showToast(
        error.response?.data?.message || "Operation failed",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // DELETE
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/rent-durations/${delModal.RentslabId}`);

      showToast("Rent duration deleted");

      setDelModal(null);

      // REFRESH PAGINATION
      const remainingRecords = total - 1;

      const newTotalPages = Math.ceil(remainingRecords / limit);

      // IF CURRENT PAGE BECOMES EMPTY,
      // MOVE TO PREVIOUS PAGE
      if (page > 1 && page > newTotalPages) {
        setPage(Math.max(1, page - 1));
      } else {
        await fetchAll();
      }

      resetForm();
    } catch (error) {
      console.error("Delete error:", error);

      showToast(
        error.response?.data?.message || "Delete failed",
        "error"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // FORMAT RANGE
  const formatRange = (rec) => {
    if (Number(rec.ToRentslab) === 1000) {
      return `${rec.FromRentslab} Days & Above`;
    }

    return `${rec.FromRentslab} – ${rec.ToRentslab} Days`;
  };

  // LATEST RECORD
  const latestRecordId =
    page === 1 && records.length > 0
      ? records[0].RentslabId
      : null;

 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  // RETURN
  return (
    <Layout activePage="Rent Durations">
      <div className="um-root">
        <main className="dashboard-main">
          {/* TABLE */}
          <div className="table-card">
            <div className="table-card-head">
              <h3 className="table-card-title d-flex align-items-center">
                <FiClipboard size={26} className="me-2" />

                <span>Manage Rent Durations</span>

                <span className="rec-count ms-1">
                  {total} slabs
                </span>
              </h3>

              {/* ADD BUTTON */}
              <button
                type="button"
                className="um-btn-primary"
                onClick={openAdd}
              >
                <span className="um-add-icon">＋</span>
                Add Rent Duration
              </button>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
              <table className="table table-striped table-hover table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr className="um-table-head">
                    {["#", "From Day", "To Day", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="um-th text-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* LOADING */}
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <td
                            key={j}
                            className="um-td text-center"
                          >
                            <div className="skeleton" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : records.length === 0 ? (
                    /* EMPTY */
                    <tr>
                      <td
                        colSpan={4}
                        className="empty-table-cell text-center py-5"
                      >
                        <div className="empty-icon">⏱️</div>

                        <div className="empty-title">
                          {search
                            ? "No matching rent durations found"
                            : "No rent durations added yet"}
                        </div>

                        <div className="empty-sub">
                          {search
                            ? "Try another search"
                            : "Use the form above to add your first slab"}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    /* RECORDS */
                    records.map((rec, i) => {
                      const isMax =
                        rec.RentslabId === latestRecordId;

                      const isToMore =
                        Number(rec.ToRentslab) === 1000;

                      return (
                        <tr
                          key={rec.RentslabId}
                          className={`um-row ${
                            editId === rec.RentslabId
                              ? "editing-row"
                              : i % 2 === 0
                              ? "even-row"
                              : "odd-row"
                          }`}
                        >
                          {/* NUMBER */}
                          <td className="um-td user-number text-center">
                            {(page - 1) * limit + i + 1}
                          </td>

                          {/* FROM */}
                          <td className="um-td text-center">
                            <span className="day-badge from-badge">
                              {rec.FromRentslab} Day
                            </span>
                          </td>

                          {/* TO */}
                          <td className="um-td text-center">
                            <span
                              className={`day-badge ${
                                isToMore
                                  ? "to-more-badge"
                                  : "to-day-badge"
                              }`}
                            >
                              {isToMore
                                ? `${rec.ToRentslab} To More Days`
                                : `${rec.ToRentslab} Day`}
                            </span>
                          </td>

                          {/* ACTIONS */}
                          <td className="um-td text-center">
                            <div className="action-buttons d-flex justify-content-center align-items-center gap-2">
                              {/* EDIT */}
                              <button
                                className="action-btn action-edit btn btn-sm"
                                title="Edit"
                                onClick={() => startEdit(rec)}
                              >
                                <FiEdit size={15} />
                              </button>

                              {/* DELETE */}
                              <button
                                className={`action-btn action-delete btn btn-sm ${
                                  isMax
                                    ? "delete-enabled"
                                    : "delete-disabled"
                                }`}
                                title={
                                  isMax
                                    ? "Delete"
                                    : "Only the latest slab can be deleted"
                                }
                                onClick={() => {
                                  if (isMax) {
                                    setDelModal(rec);
                                  }
                                }}
                              >
                                <FiTrash2 size={15} />
                              </button>

                              {!isMax && (
                                <span className="delete-hint text-muted">
                                  Delete latest first
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* PAGINATION */}
             <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={10}
                loading={loading}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </div>
          </div>
        </main>

        {/* ADD / EDIT MODAL */}
        {showModal && (
          <div
            className="um-overlay"
            onClick={closeModal}
          >
            <div
              className="um-modal rent-duration-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId
                        ? "Edit Rent Duration"
                        : "Add Rent Duration"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      {editId
                        ? "(Modify the selected duration slab below)"
                        : hasToMore
                        ? '(A "To More Days" slab already exists. Delete it first to add more.)'
                        : `(Next From value starts at Day ${nextFrom})`}
                    </p>
                  </div>
                </div>

                {/* CLOSE */}
                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}
              <div className="um-modal-scroll">
                {/* WARNING */}
                {hasToMore && !editId ? (
                  <div className="warning-box">
                    ⚠️ A{" "}
                    <strong>
                      "To More Days ({toMoreValue})"
                    </strong>{" "}
                    slab already exists. Delete the latest slab
                    first to add a new one.
                  </div>
                ) : (
                  <div className="row g-3">
                    {/* FROM DAYS */}
                    <div className="field-box col-md-3">
                      <label className="field-label">
                        FROM — Days *
                      </label>

                      <input
                        className="rd-input from-input"
                        type="number"
                        min="1"
                        max="999"
                        placeholder="e.g. 1"
                        value={form.FromRentslab}
                        onChange={handleFromChange}
                      />

                      <div className="field-hint">
                        Starting day of this slab
                      </div>
                    </div>

                    {/* TO DAYS */}
                    {!form.toMore && (
                      <div className="field-box col-md-3">
                        <label className="field-label">
                          TO — Days *
                        </label>

                        <input
                          className="rd-input"
                          type="number"
                          min="1"
                          max="999"
                          placeholder="e.g. 6"
                          value={form.ToRentslab}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              ToRentslab: e.target.value,
                            }))
                          }
                        />

                        <div className="field-hint">
                          Ending day of this slab
                        </div>
                      </div>
                    )}

                    {/* OPEN ENDED */}
                    <div className="field-box col-md-3">
                      <label className="field-label">
                        OPEN-ENDED
                      </label>

                      <select
                        className="rd-input"
                        value={form.toMore ? "1" : "0"}
                        onChange={handleToMoreChange}
                      >
                        <option value="0">Days</option>
                        <option value="1">
                          To More Days
                        </option>
                      </select>
                    </div>

                    {/* SUBMIT */}
                    <div className="field-box submit-field col-md-3">
                      <label className="field-label">
                        &nbsp;
                      </label>

                      <button
                        type="button"
                        className="um-btn-primary"
                        onClick={handleSubmit}
                        disabled={saving}
                      >
                        {saving ? (
                          <span className="spinner" />
                        ) : editId ? (
                          <>
                            <FiSave />
                            Update Slab
                          </>
                        ) : (
                          <>
                            <FiPlus />
                            Add Slab
                          </>
                        )}
                      </button>

                      {editId && (
                        <button
                          type="button"
                          className="rd-btn-secondary clear-btn"
                          onClick={closeModal}
                          disabled={saving}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {delModal && (
          <div
            className="um-overlay"
            onClick={() => setDelModal(null)}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">
                  Delete Slab?
                </h2>

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    {formatRange(delModal)}
                  </span>
                  ?
                  <br />
                  This will also remove related tariff records.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                <div className="delete-actions">
                  {/* CANCEL */}
                  <button
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  {/* DELETE */}
                  <button
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        
      </div>
    </Layout>
  );
}
